import { useMemo, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useBreakpoints } from 'common/hooks/use-breakpoints';
import { getIsFallback } from 'features/products/ducks/selectors';
import { SponsoredProductType } from 'features/products/types';
import { Product } from 'common/components/product-card/types';
import { RootState } from 'typesafe-actions';
import { SponsoredCarouselProps } from './types';
import { detectDeviceFromUserAgent, getLimitedSponsoredProducts } from './utils';

export const useSponsoredProducts = (
  fetchedProducts: Product[] | SponsoredProductType[] | null,
  sponsoredProps?: SponsoredCarouselProps,
) => {
  const isResponseFallback = useSelector(getIsFallback);
  const userAgentFromState = useSelector(({ userAgent }: RootState) => userAgent);
  const breakpoints = useBreakpoints();

  const deviceInfo = useMemo(() => {
    if (typeof window !== 'undefined' && breakpoints) {
      return {
        isMobile: breakpoints.smMobile || breakpoints.mobile,
        isDesktop: breakpoints.tablet || breakpoints.desktop || breakpoints.largeDesktop,
      };
    }
    return detectDeviceFromUserAgent(userAgentFromState);
  }, [breakpoints, userAgentFromState]);

  const { isMobile, isDesktop } = deviceInfo;

  const { products, isFallback } = useMemo(() => {
    if (!fetchedProducts || fetchedProducts.length === 0) {
      return { products: [], isFallback: true };
    }

    const { sponsoredList, fallbackList } = fetchedProducts.reduce(
      (acc, product) => {
        if ((product as SponsoredProductType).monetization) {
          acc.sponsoredList.push(product as SponsoredProductType);
        } else {
          acc.fallbackList.push(product);
        }
        return acc;
      },
      { sponsoredList: [] as SponsoredProductType[], fallbackList: [] as Product[] },
    );

    const limitedSponsoredProducts =
      sponsoredProps?.limitMinMobile || sponsoredProps?.limitMaxMobile
        ? getLimitedSponsoredProducts(sponsoredList, { isMobile, isDesktop }, sponsoredProps)
        : sponsoredList;

    const isFallbackInternal = isResponseFallback || limitedSponsoredProducts.length === 0;

    let resultProducts: Product[] = [];

    if (sponsoredProps?.useFallback) {
      resultProducts = isFallbackInternal ? fallbackList : limitedSponsoredProducts;
    } else {
      resultProducts = [...limitedSponsoredProducts, ...fallbackList];
    }

    return {
      products: resultProducts,
      isFallback: isFallbackInternal,
    };
  }, [fetchedProducts, isMobile, isDesktop, isResponseFallback, sponsoredProps]);

  const shouldShowSponsored = useCallback(
    (props?: SponsoredCarouselProps): props is SponsoredCarouselProps =>
      !!props && !!props.useFallback && !isFallback,
    [isFallback],
  );

  return {
    products,
    shouldShowSponsored,
  };
};
